import React, { useState, useEffect, useMemo, useCallback } from 'react'; // ✅ Añadido useCallback
import { useAuth } from '../context/AuthContext'; 
import {
  Box, Typography, Button, Card, CardContent, CardActions, Grid, TextField, 
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, 
  Autocomplete, Chip, Paper, IconButton, Tabs, Tab, InputAdornment, Alert,
  Snackbar 
} from '@mui/material';
import { 
    Add as AddIcon, 
    Search as SearchIcon, 
    Edit as EditIcon, 
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    MedicalServices as MedicalServicesIcon,
    Healing as HealingIcon,
    FlashOn as FlashOnIcon,
    Warning as WarningIcon 
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// Constante de especialidades (se mantiene igual)
const SPECIALTIES = [
    { name: "Anestesiología", desc: "Manejo del dolor y anestesia." },
    { name: "Anatomía Patológica", desc: "Análisis de tejidos y biopsias." },
    { name: "Cardiología", desc: "Enfermedades del corazón y sistema circulatorio." },
    { name: "Cardiología Pediátrica", desc: "Corazón en niños." },
    { name: "Cirugía Bariátrica", desc: "Tratamiento quirúrgico de la obesidad." },
    { name: "Cirugía Bucal", desc: "Cirugía de boca y mandíbula." },
    { name: "Cirugía Cardiovascular", desc: "Corazón y grandes vasos." },
    { name: "Cirugía General", desc: "Abdomen, cuello, mama, tejidos blandos." },
    { name: "Cirugía Oncológica", desc: "Cirugía de tumores." },
    { name: "Cirugía Pediátrica", desc: "Cirugía en niños." },
    { name: "Cirugía Plástica", desc: "Reconstructiva y estética." },
    { name: "Cirugía Torácica", desc: "Cirugía del tórax (no corazón)." },
    { name: "Cirugía Vascular", desc: "Arterias y venas." },
    { name: "Dermatología", desc: "Piel, pelo y uñas." },
    { name: "Endocrinología", desc: "Hormonas y metabolismo." },
    { name: "Epidemiología", desc: "Control de enfermedades en poblaciones." },
    { name: "Fisiatría y Rehabilitación", desc: "Recuperación física." },
    { name: "Gastroenterología", desc: "Aparato digestivo." },
    { name: "Genética Médica", desc: "Enfermedades hereditarias." },
    { name: "Geriatría", desc: "Atención al adulto mayor." },
    { name: "Ginecología y Obstetricia", desc: "Salud mujer, embarazo y parto." },
    { name: "Hematología", desc: "Enfermedades de la sangre." },
    { name: "Infectología", desc: "Enfermedades infecciosas." },
    { name: "Inmunología Clínica", desc: "Sistema inmunológico y alergias." },
    { name: "Medicina Crítica", desc: "Cuidados Intensivos (UCI)." },
    { name: "Medicina de Emergencia", desc: "Atención aguda." },
    { name: "Medicina Familiar", desc: "Atención integral familia/comunidad." },
    { name: "Medicina Interna", desc: "Enfermedades del adulto no quirúrgicas." },
    { name: "Medicina Nuclear", desc: "Uso de sustancias radioactivas." },
    { name: "Medicina Ocupacional", desc: "Salud en el trabajo." },
    { name: "Medicina Paliativa", desc: "Alivio del sufrimiento." },
    { name: "Nefrología", desc: "Enfermedades de los riñones." },
    { name: "Neonatología", desc: "Recién nacidos." },
    { name: "Neumonología", desc: "Enfermedades respiratorias." },
    { name: "Neurocirugía", desc: "Cirugía de cerebro, médula y nervios." },
    { name: "Neurología", desc: "Sistema nervioso central y periférico." },
    { name: "Nutrición Clínica", desc: "Manejo nutricional especializado." },
    { name: "Odontología Pediátrica", desc: "Dental niños." },
    { name: "Oftalmología", desc: "Enfermedades de los ojos." },
    { name: "Oncología Médica", desc: "Tratamiento del cáncer." },
    { name: "Oncología Radioterápica", desc: "Radioterapia." },
    { name: "Otorrinolaringología", desc: "Oído, nariz, garganta." },
    { name: "Pediatría y Puericultura", desc: "Salud de niños y adolescentes." },
    { name: "Psiquiatría", desc: "Trastornos mentales." },
    { name: "Radiología e Imagenología", desc: "Diagnóstico por imágenes." },
    { name: "Reumatología", desc: "Enfermedades autoinmunes y articulares." },
    { name: "Salud Pública", desc: "Gestión de sistemas de salud." },
    { name: "Terapia Intensiva Pediátrica", desc: "UCI Pediátrica." },
    { name: "Traumatología y Ortopedia", desc: "Sistema musculoesquelético." },
    { name: "Urología", desc: "Sistema urinario y reprod. masculino." }
].sort((a, b) => a.name.localeCompare(b.name));

const ProcedimientosMedicosPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrador';

  const [procedures, setProcedures] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(0); 
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [currentProc, setCurrentProc] = useState({ id: null, name: '', description: '', specialty: '', preparation: '' });
  const [saveMessage, setSaveMessage] = useState('');
  const [config, setConfig] = useState(null); 

  const [luiatroDialog, setLuiatroDialog] = useState({ 
      open: false, 
      content: '', 
      onConfirm: null 
  });

  // 1. CARGAR CONFIGURACIÓN
  const fetchConfig = useCallback(async () => {
    try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/config`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setConfig(await res.json());
    } catch (e) { console.error("Error al cargar config"); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // 2. FILTRADO DE ESPECIALIDADES PARA LA UI
  const filteredSpecialties = useMemo(() => {
    if (!config || !config.favoriteSpecialties) return SPECIALTIES;
    try {
        const favs = typeof config.favoriteSpecialties === 'string' 
            ? JSON.parse(config.favoriteSpecialties) 
            : config.favoriteSpecialties;
        return (Array.isArray(favs) && favs.length > 0)
            ? SPECIALTIES.filter(s => favs.includes(s.name)) 
            : SPECIALTIES;
    } catch (e) { return SPECIALTIES; }
  }, [config]);

  // 3. FUNCIONES DE BÚSQUEDA (Refactorizadas para ser llamadas desde varios sitios)
  const loadProcedures = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    try {
        let url = `${API_URL}/api/procedures?take=2000`;
        
        if (viewMode === 0) { // Modo búsqueda general
            if (search.length >= 2) {
                url += `&q=${encodeURIComponent(search)}`;
                if (config?.favoriteSpecialties) {
                    url += `&specs=${encodeURIComponent(config.favoriteSpecialties)}`;
                }
            } else {
                setProcedures([]);
                setLoading(false);
                return;
            }
        } else if (viewMode === 1 && selectedSpecialty) { // Modo especialidad
            url += `&specialty=${encodeURIComponent(selectedSpecialty.name)}`;
        }

        const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if(res.ok) setProcedures(await res.json());
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [search, viewMode, selectedSpecialty, config]);

  // Disparar búsqueda al escribir o cambiar pestaña
  useEffect(() => {
    const timer = setTimeout(() => { loadProcedures(); }, 500);
    return () => clearTimeout(timer);
  }, [loadProcedures]);

  // 4. ACCIONES (Guardar / Borrar)
  const handleSave = async () => {
      if(!currentProc.name || !currentProc.specialty) return alert("Campos obligatorios faltantes.");
      
      const isEditing = !!currentProc.id;
      const url = isEditing ? `${API_URL}/api/procedures/${currentProc.id}` : `${API_URL}/api/procedures`;
      const token = localStorage.getItem('token');

      try {
          const res = await fetch(url, {
              method: isEditing ? 'PUT' : 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ ...currentProc, createdBy: user?.id })
          });

          if (res.ok) {
              setOpenForm(false);
              setSaveMessage("✅ Catálogo actualizado");
              loadProcedures(); // ✅ Recarga limpia
          } else {
              const err = await res.json();
              alert(`Error: ${err.error || "No se pudo guardar"}`);
          }
      } catch(e) { alert("Error de red al intentar guardar"); }
  };

  const handleDelete = (id) => {
    setLuiatroDialog({
        open: true,
        content: "¿Está seguro de que desea eliminar este procedimiento del catálogo? Esta acción no se puede deshacer.",
        onConfirm: async () => {
            const token = localStorage.getItem('token');
            try {
                const res = await fetch(`${API_URL}/api/procedures/${id}`, { 
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    setSaveMessage("🗑️ Registro eliminado");
                    loadProcedures(); // ✅ Recarga limpia
                }
            } catch (error) { console.error(error); }
            setLuiatroDialog(prev => ({ ...prev, open: false }));
        }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display:'flex', justifyContent:'space-between', mb:4, alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 2 }}>
            <HealingIcon fontSize="large" color="primary"/> Catálogo de Procedimientos
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => {
            const defaultSpec = filteredSpecialties.length > 0 ? filteredSpecialties[0].name : '';
            setCurrentProc({ id: null, name: '', description: '', specialty: (selectedSpecialty?.name || defaultSpec), preparation: '' });
            setOpenForm(true);
        }}>
            Añadir Nuevo
        </Button>
      </Box>

      <Tabs value={viewMode} onChange={(_, v) => { setViewMode(v); setSelectedSpecialty(null); setProcedures([]); setSearch(''); }} sx={{ mb: 3 }}>
          <Tab label="Búsqueda General (A-Z)" icon={<SearchIcon />} iconPosition="start" />
          <Tab label="Búsqueda por Especialidad" icon={<MedicalServicesIcon />} iconPosition="start" />
      </Tabs>

      {viewMode === 0 && (
          <Box>
              <TextField 
                fullWidth sx={{ mb: 4 }} 
                placeholder="Escriba el nombre del procedimiento..." 
                value={search} onChange={e => setSearch(e.target.value)} 
                InputProps={{ startAdornment: ( <InputAdornment position="start"> <SearchIcon color="action" /> </InputAdornment> ), }}
              />
              {search.length < 2 ? (
                  <Box sx={{ py: 10, textAlign: 'center', opacity: 0.6 }}>
                      <FlashOnIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h5" fontWeight="bold">Buscador Personalizado</Typography>
                      <Typography>Mostrando resultados basados en sus especialidades favoritas.</Typography>
                  </Box>
              ) : (
                  <RenderGrid loading={loading} procedures={procedures} isAdmin={isAdmin} onEdit={(p)=>{setCurrentProc(p); setOpenForm(true)}} onDelete={handleDelete} />
              )}
          </Box>
      )}

      {viewMode === 1 && (
          <Box>
              {!selectedSpecialty ? (
                  <Grid container spacing={3}>
                      {filteredSpecialties.map((spec, i) => (
                          <Grid item xs={12} sm={6} md={4} key={i}>
                              <Paper elevation={2} onClick={() => setSelectedSpecialty(spec)} sx={{ p: 3, cursor: 'pointer', '&:hover': { bgcolor: '#e3f2fd' }, borderLeft: '5px solid #1976d2' }}>
                                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>{spec.name}</Typography>
                                  <Typography variant="body2" color="text.secondary">{spec.desc}</Typography>
                              </Paper>
                          </Grid>
                      ))}
                  </Grid>
              ) : (
                  <Box>
                      <Button startIcon={<ArrowBackIcon />} onClick={() => {setSelectedSpecialty(null); setProcedures([]);}} sx={{ mb: 2 }}>Volver</Button>
                      <Typography variant="h5" sx={{mb: 3, fontWeight: 'bold'}} color="primary">{selectedSpecialty.name}</Typography>
                      <RenderGrid loading={loading} procedures={procedures} isAdmin={isAdmin} onEdit={(p)=>{setCurrentProc(p); setOpenForm(true)}} onDelete={handleDelete} />
                  </Box>
              )}
          </Box>
      )}

      {/* MODAL FORMULARIO */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{currentProc.id ? 'Editar Procedimiento' : 'Nuevo Procedimiento'}</DialogTitle>
          <DialogContent dividers>
              <Autocomplete
                  options={SPECIALTIES}
                  getOptionLabel={(o) => o.name}
                  value={SPECIALTIES.find(s => s.name === currentProc.specialty) || null}
                  onChange={(_, nv) => setCurrentProc({...currentProc, specialty: nv ? nv.name : ''})}
                  renderInput={(p) => <TextField {...p} label="Especialidad" fullWidth sx={{ mb: 2, mt: 1 }} />}
              />
              <TextField label="Nombre" fullWidth sx={{ mb: 2 }} value={currentProc.name} onChange={e => setCurrentProc({...currentProc, name: e.target.value})} />
              <TextField label="Descripción" multiline rows={4} fullWidth value={currentProc.description} onChange={e => setCurrentProc({...currentProc, description: e.target.value})} />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
              <Button onClick={handleSave} variant="contained" color="primary">Guardar</Button>
          </DialogActions>
      </Dialog>

      <Dialog open={luiatroDialog.open} onClose={() => setLuiatroDialog(prev => ({...prev, open: false}))}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f', fontWeight: 'bold' }}>
              <WarningIcon /> Luiatro Health dice:
          </DialogTitle>
          <DialogContent dividers>
              <Typography>{luiatroDialog.content}</Typography>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setLuiatroDialog(prev => ({...prev, open: false}))}>Cancelar</Button>
              <Button variant="contained" color="error" onClick={luiatroDialog.onConfirm}>Confirmar</Button>
          </DialogActions>
      </Dialog>

      <Snackbar 
        open={!!saveMessage} 
        autoHideDuration={4000} 
        onClose={() => setSaveMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">{saveMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

// Componente auxiliar RenderGrid
const RenderGrid = ({ loading, procedures, isAdmin, onEdit, onDelete }) => (
    loading ? ( <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box> ) : (
        <Grid container spacing={3}>
            {procedures.length > 0 ? procedures.map((proc) => (
                <Grid item xs={12} sm={6} md={4} key={proc.id}>
                    <Card sx={{ height: '100%', borderRadius: 3, borderTop: '4px solid #1976d2' }}>
                        <CardContent>
                            <Chip label={proc.specialty} size="small" color="primary" sx={{ mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{proc.name}</Typography>
                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>{proc.description}</Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'flex-end', bgcolor: '#fafafa' }}>
                            <IconButton size="small" color="info" onClick={() => onEdit(proc)}><EditIcon /></IconButton>
                            {isAdmin && <IconButton size="small" color="error" onClick={() => onDelete(proc.id)}><DeleteIcon /></IconButton>}
                        </CardActions>
                    </Card>
                </Grid>
            )) : (
                <Grid item xs={12}><Alert severity="info">No se encontraron procedimientos registrados.</Alert></Grid>
            )}
        </Grid>
    )
);

export default ProcedimientosMedicosPage;